/**
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

/**
 * The BaseSummaryValueQuery contains the commone elements for a single query that updates summaryCollector entries
 * with the single row result of the aggregated summary values with filtering applied
 * for all measures (ordinals) that are projected.
 * NOTE: the execute method contains Logic to process exception conditions
 *		to avoid querying and/or update summaries immediately if possible.
 **/
class BaseSummarizerQuery {

	/**
	 * @Constructor
	 * @param {Object} widgetApi - the owner widget (needed to access services)
	 * @param {Object} dashboardApi - the dashboardApi
	 * @param {Object} logger - The logger
	 * @param {Function} [cbGetRowTuples=null] - when this callback is supplied, the rendered data rows are used
	 *				where the filter values are "implied" (eg TopN).  This is only needed for summary stats (not selection stats)
	 */
	constructor(widgetApi, dashboardApi, logger, cbGetRowTuples=null) {
		this.widgetApi=widgetApi;
		this.dashboardApi=dashboardApi;

		this.logger=logger;
		this.cbGetRowTuples = cbGetRowTuples;

		//By default, we should include the filter context.
		this.includeFilters = true;
	}

	destroy() {
		this.deactivate();
	}

	activate() {
		//If the widget shaping changed, we need to refresh summaries.
		this.widgetApi.on('shapingmodel:changed', this.onWidgetShapingChanged, this);
	}

	deactivate() {
		//If the widget shaping changed, we need to refresh summaries.
		this.widgetApi.off('shapingmodel:changed', this.onWidgetShapingChanged, this);
	}

	onWidgetShapingChanged() {
		//Perform any actions necessary if notified that the widget shaping changed.
		//For example, if the widget shaping changed, we need to refresh summaries.
		//By default, this method does nothing.
	}

	addQueryItem() {
	}

	/**
	 * execute a query (or return known/cached results) for the Summary values of each fact (measure).
	 * NOTE: This function often doesn't actually execute a query if Summaries don't change or
	 *  values can be returned for specialconditions processed in "addQueryItem".
	 * @param {Object} summaryCollector - a summaryCollector object (or selectionSummaryCollector) whose entries are to be updated with the results.
	 * @param {Object} queryDef (optional) - a queryDefinition to build onto (for combined summaries)
	 * @param {Boolean} [isSelectionStats=false] - when true, stats collected are for selections.
	 */
	build(summaryCollector, queryDef={ dataItems: [], projections: [] }, isSelectionStats=false) {
		const content = this.dashboardApi.getCanvas().getContent(this.widgetApi.getId());
		const visualizationAPI = this.widgetApi.getVisualization();
		const savedPromptsAPI = visualizationAPI.getSavedPrompts();
		if (savedPromptsAPI && savedPromptsAPI.getPromptSpecList()) {
			queryDef.parameterValues = savedPromptsAPI.getPromptSpecList();
		}
		isSelectionStats = isSelectionStats || !!summaryCollector.isSelectionSummaryCollector;
		//Extract all projected ordinal data items with aggregate set to produce a single row query result.
		const slotsAPI = visualizationAPI.getSlots();
		if (slotsAPI) {
			slotsAPI.getDataItemList().forEach((mappedDataItem) => {
				this.addQueryItem(queryDef, mappedDataItem, summaryCollector);
			});
		}

		if (queryDef.dataItems.length === 0 || queryDef.projections.length === 0) {
			//If nothing was included in the query for whatever reason, return null.
			return null;
		}

		if (this.includeFilters) {
			const addFiltersResult = this.addFilters(content, isSelectionStats);
			if (!addFiltersResult.canBuildFilterSpec) {
				queryDef.dataItems = [];	//Reset the items to an empty list and return null to indicate the query can't be done.
				return null;
			}
			queryDef.filters = addFiltersResult.filters;
		}
		return queryDef;
	}

	execute(summaryCollector) {
		const queryDef = this.build(summaryCollector);
		if (!queryDef) {
			return Promise.resolve(true);
		}

		//Query for any overall summaries that are not already cached
		return this._execute(summaryCollector, queryDef);
	}

	_execute(summaryCollector, queryDef) {
		return this._getColumnValues(queryDef).then((queryResultData) => {
			queryResultData.getResultItemList().forEach((resultDataItem, colIdx) => {
				const dataItemUniqueId = queryDef.projections[colIdx];
				const tupleHeader = resultDataItem.getDataItemList();
				const cellValue = queryResultData.getValue(0, colIdx);
				if (cellValue) {
					const finalValue = cellValue.getValue && cellValue.getValue() || cellValue.v;
					this.processValue(dataItemUniqueId, tupleHeader[0].aggregate, finalValue, summaryCollector);
				}
			});
			return true;
		}).catch((error) => {
			this._logError(error);
			//don't throw here!  If the query fails, processValue isn't called (empty response)
			//but it shouldn't fail the visualization query.
		});
	}

	/**
	 * Update the summary entry with the summary value under the fieldNames specified.
	 * @param {Object} summaryCollector - the summaryCollector to be updated
	 * @param {String} dataItemUniqueId - The id of the dataItem to update the summary entry for.
	 * @param {String} overallSummaryValue - the single row value to update the entry with
	 * @param {String} fieldNameUse - the field name to update with the unformatted value.
	 * @param {String} fieldName - the field name to update with the formatted value.
	 */
	updateSummaryEntry(summaryCollector, dataItemUniqueId, overallSummaryValue, fieldNameUse, fieldName) {
		let summaryEntry = summaryCollector.getSummary(dataItemUniqueId);
		if (summaryEntry) {
			if (summaryCollector.isSelectionSummaryCollector && !summaryEntry.valueMap) {
				summaryCollector.removeSummary(dataItemUniqueId);
				summaryEntry = null;
			} else {
				//before update the summary, call updateSummaryEntry to replace the current
				//summaryEntry with the copy of itself, see SummaryCollector.updateSummaryEntry for details
				summaryEntry = summaryCollector.updateSummaryEntry(summaryEntry);
				summaryEntry[fieldNameUse]=overallSummaryValue;

				const unformattedSummaryValue = (overallSummaryValue===0) ? '0' : (overallSummaryValue || '').toString();
				const formatSpec=summaryEntry.metadata && summaryEntry.metadata.format;
				const Formatter = this._getFormatter();
				summaryEntry[fieldName] =  (Formatter && formatSpec)
					? Formatter.format(summaryEntry[fieldNameUse], formatSpec)
					: unformattedSummaryValue;
			}
		}
		return summaryEntry;
	}

	/**
	 * add Filters to the query.
	 * @param {boolean} isSelectionStats - true if filters are being added for visible selections
	 * @returns An object with a status (canBuildFilterSpec) and, if true, the FilterJSON suitable for sending to the query api.
	 */
	addFilters(content, isSelectionStats) {
		const filters = [];

		//NOTE: The pinningAPI has a flag to exclude visible selections so we want to INCLUDE them only when asking for selectionSummaries.
		const dataPointSelections = content.getFeature('DataPointSelections.deprecated');
		const selections = dataPointSelections.getSelections();
		const portableFilters = this._getFilterInfo(content);
		const useRowTuplesAsFilters = this._useRowTuplesAsFilters(portableFilters);

		if (selections.length > 0) {
			filters.push(this._buildDataPointFilterFromSelections(selections));
		}

		if (useRowTuplesAsFilters && this.cbGetRowTuples) {
			const rowTuples = this.cbGetRowTuples(isSelectionStats);
			if (!rowTuples || !rowTuples.tuples || rowTuples.tuples.length === 0) {
				//Can't build a filter spec if we need tuples and the returned tuples are empty.
				return { canBuildFilterSpec: false };
			}
			if (rowTuples.itemIds.length === 1) {
				const tuples = rowTuples.tuples.map((tuple) => tuple[0]);
				filters.push({ columnId: rowTuples.itemIds[0], operator: 'in', values: tuples });
			} else {
				filters.push(this._buildDataPointFilter(rowTuples));
			}
		}

		portableFilters && portableFilters.forEach && portableFilters.forEach((filterEntry) => {
			//post-aggregate range filters are covered by rendered tuples.  Don't include them as filters.
			if (!this._isPostAggregateRangeFilter(filterEntry)) {
				filters.push(filterEntry);
			}
		});
		return { canBuildFilterSpec: true, filters };
	}

	_buildDataPointFilterFromSelections(selections) {
		const orFilter = {
			operator: 'or',
			values: []
		};

		selections.forEach((selection) => {
			const andFilter = { operator: 'and', values: [] };
			selection.categories.forEach((tuplePart) => {
				//TODO: guard against post-aggregate filters.
				andFilter.values.push({ columnId: tuplePart.columnId, operator: 'in', values: [ { d: tuplePart.label, label: tuplePart.label } ] });
			});
			orFilter.values.push(andFilter);
		});
		return orFilter;
	}

	_buildDataPointFilter(rowTuples) {
		const itemIds = rowTuples.itemIds;
		const orFilter = {
			operator: 'or',
			values: []
		};

		rowTuples.tuples.forEach((rowTuple) => {
			const andFilter = { operator: 'and', values: [] };
			rowTuple.forEach((tuplePart, indexInSlot) => {
				//TODO: guard against post-aggregate filters.
				andFilter.values.push({ columnId: itemIds[indexInSlot], operator: 'in', values: [tuplePart] });
			});
			orFilter.values.push(andFilter);
		});
		return orFilter;
	}

	_isPostAggregateRangeFilter(filterEntry) {
		return filterEntry.preOrPost==='post' && filterEntry.operator && this._isRangeFilter(filterEntry.operator);
	}

	_isRangeFilter(operator) {
		const rangeOps = ['between', 'notbetween', 'lt', 'gt'];
		return rangeOps.indexOf(operator) > -1;
	}

	//return true if this widget requires the rendered tuples to determine filter values.
	//This can happen in cases where the filter values are implied (ie: TopN/BottomN or post aggregate range filers)
	_useRowTuplesAsFilters(portableFilters) {
		return this._hasTopBottom() || this._hasPostAggregateRangeFilters(portableFilters);
	}

	//return true if this widget includes topBottom (and we need to use the rendered row tuples as filters.)
	_hasTopBottom() {
		const content = this.dashboardApi.getCanvas().getContent(this.widgetApi.getId());
		const visualization = content.getFeature('Visualization');
		return !!visualization.getSlots().getDataItemList().find(dataItem => dataItem.getTopBottom());
	}

	//return true if this widget includes post aggregate range filters (and we need to use the rendered row tuples as filters.)
	_hasPostAggregateRangeFilters(portableFilters) {
		//post-aggregate range filters also reduce the set based on aggregated group values.
		const hasPostAggregateRangeFilter = portableFilters &&
			portableFilters.some((filterEntry) => this._isPostAggregateRangeFilter(filterEntry));
		return !!hasPostAggregateRangeFilter;
	}

	_getFormatter() {
		return this.widgetApi.getVisApi().getFormatter();
	}

	_getFilterInfo (content) {
		const globalFilters = this.dashboardApi.getFeature('GlobalFilters');
		const eventGroups = this.dashboardApi.getFeature('EventGroups');
		const visualization = content.getFeature('Visualization');
		const datasource = visualization.getDataSource();
		const localFilters = visualization.getLocalFilters();
		const type = this.dashboardApi.getAppConfig('pageContainerType');
		const combinedFilterList = [];
		combinedFilterList.push(...localFilters.getFilterList());
		combinedFilterList.push(...globalFilters.getFilterList({
			origin: 'filter',
			sourceId: datasource.getId(),
			scope: this._getPageContent(content, type).getId(),
			eventGroupId: eventGroups.getGroupId(content.getId())
		}));
		return combinedFilterList;
	}

	_getColumnValues(queryDef) {
		const content = this.dashboardApi.getCanvas().getContent(this.widgetApi.getId());
		const visualization = content.getFeature('Visualization');
		const queryService = this.dashboardApi.getFeature('QueryService');
		const dataSource = visualization.getDataSource();
		const aggregations = queryDef.dataItems.map((dataItem) => dataItem.aggregate);
		const columnIds = queryDef.dataItems.map((dataItem) => dataItem.itemId);
		return queryService.getColumnValues(dataSource.getId(), columnIds, queryDef.filters, undefined, [], aggregations);
	}

	/**
	 * TODO: This method is from dashboard-common/ContentUtil.js we should find a better way managing dependencies across micro components
	 * Recursively walk the content and it's parent to find the page content.
	 * @param {ContentAPI} content
	 * @param {String} [type='page'] the content type.
	 * @returns {ContentAPI} page content. If the type can not be found, returns null
	 */
	_getPageContent(content, type = 'page') {
		let pageContent = null;
		while(content) {
			if ((type && content.getType) && (content.getType() === type)) {
				pageContent = content;
			}
			content = (content.getContainer && content.getContainer());
		}
		return pageContent;
	}

	_logError() {
		this.logger.error('An error occured while requesting measure summaries for statistics', arguments, this.widgetApi.getVisApi());
	}
}

export default BaseSummarizerQuery;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-summarizer/src/js/BaseSummarizerQuery.js